import { localStorageKeys, LogSentryEvent, ServiceError } from "@repo/core-utils";

export interface RequestClientParams {
  baseUrl: string;
  authBaseUrl?: string;
  logoutCallback?: () => void;
  defaultHeaders: Record<string, string>;
  audience: string;
  loggerFn?: typeof console.log;
  refreshTokenKey?: string;
  accessTokenKey?: string;
}

export interface CreateRequestParams {
  method: string;
  url: URL | string;
  extraHeaders?: { [key: string]: string };
  body?: unknown;
  abortSignal?: AbortSignal;
  baseUrl?: string;
}

export interface RequestParams extends CreateRequestParams {
  /**
   * @deprecated Use useQuery retry logic instead
   */
  retries?: number;
}

export class ResponseAPI {
  private response: Response;
  public constructor(response: Response) {
    this.response = response;
  }

  public json<T>(): Promise<T> {
    return this.response.json();
  }

  public blob(progressCallback?: (progress: number) => void): Promise<Blob> {
    const total = parseInt(this.response.headers.get("content-length") ?? "0", 10);
    let loaded = 0;
    const res = new Response(
      new ReadableStream({
        start: async (controller) => {
          const reader = this.response?.body?.getReader();
          if (reader) {
            for (;;) {
              const { done, value } = await reader.read();
              if (done) break;
              if (value) {
                loaded += value?.byteLength ?? 0;

                progressCallback?.(Math.min(Math.round((loaded / total) * 100), 100));
                controller.enqueue(value);
              }
            }
          }
          controller.close();
        },
      })
    );
    return res.blob();
  }
}

export class RequestClient {
  private baseUrl?: RequestClientParams["baseUrl"];
  private authBaseUrl?: RequestClientParams["authBaseUrl"];
  private logoutCallback?: RequestClientParams["logoutCallback"];
  private defaultHeaders: RequestClientParams["defaultHeaders"];
  private audience: RequestClientParams["audience"];
  private loggerFn?: RequestClientParams["loggerFn"];
  private refreshTokenKey: string;
  private accessTokenKey: string;

  public constructor({
    baseUrl,
    authBaseUrl,
    logoutCallback,
    defaultHeaders,
    audience,
    loggerFn,
    refreshTokenKey,
    accessTokenKey,
  }: RequestClientParams) {
    this.baseUrl = baseUrl;
    this.authBaseUrl = authBaseUrl ?? baseUrl;
    this.logoutCallback = logoutCallback;
    this.defaultHeaders = defaultHeaders;
    this.audience = audience;
    this.loggerFn = loggerFn;
    this.refreshTokenKey = refreshTokenKey ?? localStorageKeys.refreshToken;
    this.accessTokenKey = accessTokenKey ?? localStorageKeys.accessToken;
  }

  private createRequest = ({
    method,
    url,
    extraHeaders,
    body,
    abortSignal,
    baseUrl,
  }: CreateRequestParams) => {
    const headers = new Headers({ ...this.defaultHeaders });

    if (extraHeaders) {
      for (const headerKey of Object.keys(extraHeaders)) {
        headers.set(headerKey, extraHeaders[headerKey]);
      }
    }

    let parsedUrl: URL | string = "";

    if (typeof url === "string") {
      try {
        parsedUrl = new URL(url);
      } catch (err) {
        parsedUrl = `${baseUrl ?? this.baseUrl}${url}`;
      }
    } else {
      parsedUrl = url;
    }

    const request = new Request(parsedUrl, {
      body: JSON.stringify(body),
      headers: new Headers(headers),
      method,
      mode: "cors",
      signal: abortSignal,
    });

    this.loggerFn?.("createRequest - ", request);
    return request;
  };

  public publicRequest = async ({
    method,
    url,
    extraHeaders,
    body,
    abortSignal,
    retries,
  }: RequestParams): Promise<ResponseAPI> => {
    return fetch(this.createRequest({ method, url, extraHeaders, body, abortSignal }))
      .then(async (response: Response) => {
        if (response.status < 200 || response.status >= 300) {
          throw await ServiceError.fromFetchResponse(response);
        }
        return new ResponseAPI(response);
      })
      .catch((error) => {
        if (retries && retries > 1) {
          return this.publicRequest({
            method,
            url,
            extraHeaders,
            body,
            abortSignal,
            retries: retries - 1,
          });
        } else {
          return Promise.reject(error);
        }
      });
  };

  public authenticatedNonRefreshingRequest = async ({
    method,
    url,
    extraHeaders,
    body,
    abortSignal,
  }: RequestParams): Promise<ResponseAPI> => {
    if (!this.getAccessToken()) {
      this.logoutCallback?.();
      this.loggerFn?.("authenticatedRequest error - ", {
        method,
        url: url.toString(),
        headers: extraHeaders ?? {},
        reason: "no_access_token",
      });
      throw new Error("Missing access token");
    }
    const authHeaders = {
      Authorization: "Bearer " + this.getAccessToken(),
    };

    const requestParams = {
      method,
      url,
      extraHeaders: { ...extraHeaders, ...authHeaders },
      body,
      abortSignal,
    };

    const response = await fetch(this.createRequest(requestParams));

    if (response.status === 401) {
      this.loggerFn?.("authenticatedRequest: unauthorized response - ", requestParams);

      const { code } = await response.json();
      if (code !== "AUTH_TOKEN_EXPIRED" && code !== "INVALID_TOKEN") {
        LogSentryEvent({ messagePrefix: "Unexpected token error", error: code });
      }

      this.logoutCallback?.();

      throw await ServiceError.fromFetchResponse(response);
    }
    if (!response.ok) {
      throw await ServiceError.fromFetchResponse(response);
    }
    return new ResponseAPI(response);
  };

  public authenticatedRequest = async ({
    method,
    url,
    extraHeaders,
    body,
    abortSignal,
    retries,
  }: RequestParams): Promise<ResponseAPI> => {
    if (!this.getRefreshToken()) {
      this.logoutCallback?.();
      this.loggerFn?.("authenticatedRequest error - ", {
        method,
        url: url.toString(),
        headers: extraHeaders ?? {},
        reason: "no_refresh_token",
      });
      // Use AbortError so Sentry drops this
      const missingTokenError = new Error("Missing refresh token");
      missingTokenError.name = "AbortError";
      throw missingTokenError;
    }

    const authHeaders = {
      Authorization: "Bearer " + this.getAccessToken(),
    };

    const requestParams = {
      method,
      url,
      extraHeaders: { ...extraHeaders, ...authHeaders },
      body,
      abortSignal,
    };

    return await fetch(this.createRequest(requestParams))
      .then(async (response) => {
        if (response.status === 401) {
          this.loggerFn?.("authenticatedRequest: unauthorized response - ", requestParams);

          const { code } = await response.json();
          if (code !== "AUTH_TOKEN_EXPIRED" && code !== "INVALID_TOKEN") {
            LogSentryEvent({ messagePrefix: "Unexpected token error", error: code });
          }

          // trying to refresh the token with best effort
          const tokenRefreshResult = await this.refreshTokens({
            abortSignal,
            onFail: this.logoutCallback,
          });
          if (!tokenRefreshResult) {
            throw await ServiceError.fromFetchResponse(response);
          }

          const authHeaders = {
            Authorization: "Bearer " + this.getAccessToken(),
          };
          response = await fetch(
            this.createRequest({
              method,
              url,
              extraHeaders: { ...extraHeaders, ...authHeaders },
              body,
              abortSignal,
            })
          );
        }
        if (!response.ok) {
          throw await ServiceError.fromFetchResponse(response);
        }
        return new ResponseAPI(response);
      })
      .catch((err) => {
        if (retries && retries > 1) {
          return this.authenticatedRequest({
            method,
            url,
            extraHeaders,
            body,
            abortSignal,
            retries: retries - 1,
          });
        } else {
          return Promise.reject(err);
        }
      });
  };

  private refreshTokens = async ({
    abortSignal,
    onFail,
  }: {
    abortSignal?: AbortSignal;
    onFail?: () => void;
  }): Promise<boolean> => {
    const refreshToken = this.getRefreshToken();
    const accessToken = this.getAccessToken();

    if (!refreshToken && !accessToken) {
      onFail?.();
      // User already fully logged out, no way to refresh the token
      return false;
    }

    const authHeaders = {
      Authorization: "Bearer " + refreshToken,
    };
    const body = {
      audience: this.audience,
    };

    try {
      const response = await fetch(
        this.createRequest({
          method: "POST",
          url: "/auth/refresh-token",
          extraHeaders: authHeaders,
          body,
          abortSignal,
          baseUrl: this.authBaseUrl,
        })
      );
      if (response.status === 200) {
        const { refreshToken, accessToken } = await response.json();
        this.setTokens(refreshToken, accessToken);
        return true;
      } else {
        onFail?.();
        this.loggerFn?.("refreshTokens error - ", {
          method: "POST",
          url: "/auth/refresh-token",
          headers: authHeaders,
        });
        return false;
      }
    } catch (err) {
      onFail?.();
      return false;
    }
  };

  private setTokens = (refreshToken: string, accessToken: string) => {
    window.localStorage.setItem(this.refreshTokenKey, refreshToken);
    window.localStorage.setItem(this.accessTokenKey, accessToken);
  };

  private getRefreshToken = () => {
    return window.localStorage.getItem(this.refreshTokenKey);
  };
  private getAccessToken = () => {
    return window.localStorage.getItem(this.accessTokenKey);
  };
}
